import { matchPath } from "react-router-dom";
import type { BreadcrumbItem } from "Helpers/generalBreadcrumbs";

type BreadcrumbRule = {
  path: string;
  crumbs: (params: Record<string, string | undefined>) => BreadcrumbItem[];
};

const setup = (): BreadcrumbItem => ({
  labelKey: "businessSetup",
});

/** Business Setup sidebar module routes — more specific paths first. */
const BUSINESS_SETUP_BREADCRUMB_RULES: BreadcrumbRule[] = [
  // Business + nested stations / integrations
  {
    path: "/businesses",
    crumbs: () => [setup(), { labelKey: "businessDetails" }],
  },
  {
    path: "/business/create",
    crumbs: () => [
      setup(),
      { labelKey: "businessDetails", href: "/businesses" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/business/:id/stations/create",
    crumbs: (p) => [
      setup(),
      { labelKey: "businessDetails", href: "/businesses" },
      {
        labelKey: "Details",
        href: p.id ? `/business/${p.id}` : undefined,
      },
      {
        labelKey: "stations",
        href: p.id ? `/business/${p.id}/stations` : undefined,
      },
      { labelKey: "create" },
    ],
  },
  {
    path: "/business/:id/stations/:stationId/assign/account",
    crumbs: (p) => [
      setup(),
      { labelKey: "businessDetails", href: "/businesses" },
      {
        labelKey: "Details",
        href: p.id ? `/business/${p.id}` : undefined,
      },
      {
        labelKey: "stations",
        href: p.id ? `/business/${p.id}/stations` : undefined,
      },
      {
        labelKey: "Details",
        href:
          p.id && p.stationId
            ? `/business/${p.id}/stations/${p.stationId}`
            : undefined,
      },
      { labelKey: "assignAccount" },
    ],
  },
  {
    path: "/business/:id/stations/:stationId/assign/user",
    crumbs: (p) => [
      setup(),
      { labelKey: "businessDetails", href: "/businesses" },
      {
        labelKey: "Details",
        href: p.id ? `/business/${p.id}` : undefined,
      },
      {
        labelKey: "stations",
        href: p.id ? `/business/${p.id}/stations` : undefined,
      },
      {
        labelKey: "Details",
        href:
          p.id && p.stationId
            ? `/business/${p.id}/stations/${p.stationId}`
            : undefined,
      },
      { labelKey: "assignUser" },
    ],
  },
  {
    path: "/business/:id/stations/:stationId/terms-and-conditions",
    crumbs: (p) => [
      setup(),
      { labelKey: "businessDetails", href: "/businesses" },
      {
        labelKey: "Details",
        href: p.id ? `/business/${p.id}` : undefined,
      },
      {
        labelKey: "stations",
        href: p.id ? `/business/${p.id}/stations` : undefined,
      },
      {
        labelKey: "Details",
        href:
          p.id && p.stationId
            ? `/business/${p.id}/stations/${p.stationId}`
            : undefined,
      },
      { labelKey: "termsAndConditions" },
    ],
  },
  {
    path: "/business/:id/stations/:stationId/plan-usage",
    crumbs: (p) => [
      setup(),
      { labelKey: "businessDetails", href: "/businesses" },
      {
        labelKey: "Details",
        href: p.id ? `/business/${p.id}` : undefined,
      },
      {
        labelKey: "stations",
        href: p.id ? `/business/${p.id}/stations` : undefined,
      },
      {
        labelKey: "Details",
        href:
          p.id && p.stationId
            ? `/business/${p.id}/stations/${p.stationId}`
            : undefined,
      },
      { labelKey: "planUsage" },
    ],
  },
  {
    path: "/business/:id/stations/:stationId",
    crumbs: (p) => [
      setup(),
      { labelKey: "businessDetails", href: "/businesses" },
      {
        labelKey: "Details",
        href: p.id ? `/business/${p.id}` : undefined,
      },
      {
        labelKey: "stations",
        href: p.id ? `/business/${p.id}/stations` : undefined,
      },
      { labelKey: "Details" },
    ],
  },
  {
    path: "/business/:id/stations",
    crumbs: (p) => [
      setup(),
      { labelKey: "businessDetails", href: "/businesses" },
      {
        labelKey: "Details",
        href: p.id ? `/business/${p.id}` : undefined,
      },
      { labelKey: "stations" },
    ],
  },
  {
    path: "/business/:id/subscription-analytics",
    crumbs: (p) => [
      setup(),
      { labelKey: "businessDetails", href: "/businesses" },
      {
        labelKey: "Details",
        href: p.id ? `/business/${p.id}` : undefined,
      },
      { labelKey: "subscriptionAnalytics" },
    ],
  },
  {
    path: "/business/:id/integration/add",
    crumbs: (p) => [
      setup(),
      { labelKey: "businessDetails", href: "/businesses" },
      {
        labelKey: "Details",
        href: p.id ? `/business/${p.id}` : undefined,
      },
      {
        labelKey: "integrations",
        href: p.id ? `/business/${p.id}/integrations` : undefined,
      },
      { labelKey: "create" },
    ],
  },
  {
    path: "/business/:id/integration/:integrationId",
    crumbs: (p) => [
      setup(),
      { labelKey: "businessDetails", href: "/businesses" },
      {
        labelKey: "Details",
        href: p.id ? `/business/${p.id}` : undefined,
      },
      {
        labelKey: "integrations",
        href: p.id ? `/business/${p.id}/integrations` : undefined,
      },
      { labelKey: "Details" },
    ],
  },
  {
    path: "/business/:id/integrations",
    crumbs: (p) => [
      setup(),
      { labelKey: "businessDetails", href: "/businesses" },
      {
        labelKey: "Details",
        href: p.id ? `/business/${p.id}` : undefined,
      },
      { labelKey: "integrations" },
    ],
  },
  {
    path: "/business/:id",
    crumbs: () => [
      setup(),
      { labelKey: "businessDetails", href: "/businesses" },
      { labelKey: "Details" },
    ],
  },

  // Business templates
  {
    path: "/business-templates",
    crumbs: () => [setup(), { labelKey: "businessTemplates" }],
  },
  {
    path: "/business-template/create",
    crumbs: () => [
      setup(),
      { labelKey: "businessTemplates", href: "/business-templates" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/business-template/:id",
    crumbs: () => [
      setup(),
      { labelKey: "businessTemplates", href: "/business-templates" },
      { labelKey: "Details" },
    ],
  },

  // Accounts
  {
    path: "/accounts",
    crumbs: () => [setup(), { labelKey: "accounts" }],
  },
  {
    path: "/account/create",
    crumbs: () => [
      setup(),
      { labelKey: "accounts", href: "/accounts" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/account/:name/:id/ledger",
    crumbs: () => [
      setup(),
      { labelKey: "accounts", href: "/accounts" },
      { labelKey: "ledger" },
    ],
  },
  {
    path: "/account/:id",
    crumbs: () => [
      setup(),
      { labelKey: "accounts", href: "/accounts" },
      { labelKey: "edit" },
    ],
  },

  // Inter-account transfers
  {
    path: "/inter-account-transfers",
    crumbs: () => [setup(), { labelKey: "interAccountTransfers" }],
  },
  {
    path: "/inter-account-transfer/create",
    crumbs: () => [
      setup(),
      {
        labelKey: "interAccountTransfers",
        href: "/inter-account-transfers",
      },
      { labelKey: "create" },
    ],
  },

  // Business groups
  {
    path: "/business-groups",
    crumbs: () => [setup(), { labelKey: "businessGroup" }],
  },
  {
    path: "/business-group/create",
    crumbs: () => [
      setup(),
      { labelKey: "businessGroup", href: "/business-groups" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/business-group/assign/station/:id",
    crumbs: () => [
      setup(),
      { labelKey: "businessGroup", href: "/business-groups" },
      { labelKey: "assignStation" },
    ],
  },
  {
    path: "/business-group/:id",
    crumbs: () => [
      setup(),
      { labelKey: "businessGroup", href: "/business-groups" },
      { labelKey: "Details" },
    ],
  },

  // Expenses
  {
    path: "/expenses",
    crumbs: () => [setup(), { labelKey: "expenses" }],
  },
  {
    path: "/expense/add",
    crumbs: () => [
      setup(),
      { labelKey: "expenses", href: "/expenses" },
      { labelKey: "addExpense" },
    ],
  },
  {
    path: "/expense/view/:id",
    crumbs: () => [
      setup(),
      { labelKey: "expenses", href: "/expenses" },
      { labelKey: "view" },
    ],
  },
  {
    path: "/expense/:id",
    crumbs: () => [
      setup(),
      { labelKey: "expenses", href: "/expenses" },
      { labelKey: "Details" },
    ],
  },

  // Expense types
  {
    path: "/expense-types",
    crumbs: () => [setup(), { labelKey: "Expense Types" }],
  },
  {
    path: "/expense-type/create",
    crumbs: () => [
      setup(),
      { labelKey: "Expense Types", href: "/expense-types" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/expense-type/:id",
    crumbs: () => [
      setup(),
      { labelKey: "Expense Types", href: "/expense-types" },
      { labelKey: "Details" },
    ],
  },

  // Tax procedures
  {
    path: "/tax-procedures",
    crumbs: () => [setup(), { labelKey: "taxProcedures" }],
  },
  {
    path: "/tax-procedure/create",
    crumbs: () => [
      setup(),
      { labelKey: "taxProcedures", href: "/tax-procedures" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/tax-procedure/:id",
    crumbs: () => [
      setup(),
      { labelKey: "taxProcedures", href: "/tax-procedures" },
      { labelKey: "Details" },
    ],
  },

  // Cancellation reasons
  {
    path: "/cancellation-reasons",
    crumbs: () => [setup(), { labelKey: "cancellationReasons" }],
  },
  {
    path: "/cancellation-reason/create",
    crumbs: () => [
      setup(),
      { labelKey: "cancellationReasons", href: "/cancellation-reasons" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/cancellation-reason/:id",
    crumbs: () => [
      setup(),
      { labelKey: "cancellationReasons", href: "/cancellation-reasons" },
      { labelKey: "Details" },
    ],
  },

  // Insurance companies (+ nested policies under /insurance-company/:id)
  {
    path: "/insurance-companies",
    crumbs: () => [setup(), { labelKey: "insuranceCompanies" }],
  },
  {
    path: "/insurance-company/create",
    crumbs: () => [
      setup(),
      { labelKey: "insuranceCompanies", href: "/insurance-companies" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/insurance-company/:id/insurance-policy/create",
    crumbs: (p) => [
      setup(),
      { labelKey: "insuranceCompanies", href: "/insurance-companies" },
      {
        labelKey: "insuranceCompany",
        href: p.id ? `/insurance-company/${p.id}` : undefined,
      },
      {
        labelKey: "insurancePolicies",
        href: p.id
          ? `/insurance-company/${p.id}/insurance-policies`
          : undefined,
      },
      { labelKey: "create" },
    ],
  },
  {
    path: "/insurance-company/:id/insurance-policy/:policyId",
    crumbs: (p) => [
      setup(),
      { labelKey: "insuranceCompanies", href: "/insurance-companies" },
      {
        labelKey: "insuranceCompany",
        href: p.id ? `/insurance-company/${p.id}` : undefined,
      },
      {
        labelKey: "insurancePolicies",
        href: p.id
          ? `/insurance-company/${p.id}/insurance-policies`
          : undefined,
      },
      { labelKey: "Details" },
    ],
  },
  {
    path: "/insurance-company/:id/insurance-policies",
    crumbs: (p) => [
      setup(),
      { labelKey: "insuranceCompanies", href: "/insurance-companies" },
      {
        labelKey: "insuranceCompany",
        href: p.id ? `/insurance-company/${p.id}` : undefined,
      },
      { labelKey: "insurancePolicies" },
    ],
  },
  {
    path: "/insurance-company/:id",
    crumbs: () => [
      setup(),
      { labelKey: "insuranceCompanies", href: "/insurance-companies" },
      { labelKey: "Details" },
    ],
  },

  // Hosting details
  {
    path: "/hosting-details",
    crumbs: () => [setup(), { labelKey: "hostingDetails" }],
  },
  {
    path: "/hosting-details/create",
    crumbs: () => [
      setup(),
      { labelKey: "hostingDetails", href: "/hosting-details" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/hosting-details/:id",
    crumbs: () => [
      setup(),
      { labelKey: "hostingDetails", href: "/hosting-details" },
      { labelKey: "Details" },
    ],
  },

  // Subscription plans
  {
    path: "/subscription-plans",
    crumbs: () => [setup(), { labelKey: "subscriptionPlans" }],
  },
  {
    path: "/subscription-plans/create",
    crumbs: () => [
      setup(),
      { labelKey: "subscriptionPlans", href: "/subscription-plans" },
      { labelKey: "create" },
    ],
  },
  {
    path: "/subscription-plans/:id",
    crumbs: () => [
      setup(),
      { labelKey: "subscriptionPlans", href: "/subscription-plans" },
      { labelKey: "Details" },
    ],
  },
];

export const getBusinessSetupBreadcrumbs = (
  pathname: string,
): BreadcrumbItem[] | null => {
  for (const rule of BUSINESS_SETUP_BREADCRUMB_RULES) {
    const match = matchPath({ path: rule.path, end: true }, pathname);
    if (match) {
      return rule.crumbs(match.params as Record<string, string | undefined>);
    }
  }
  return null;
};
